import { Star } from '../../icons';
import { useFavoritesStore, FAVORITES_MAX } from '../../store/favoritesStore';
import './FavoriteStar.css';

/**
 * FavoriteStar (2026-08-21) — **어디서든 붙일 수 있는 즐겨찾기 별 토글**.
 *
 * 배경: 프로젝트 좌측 2차 네비에 '즐겨찾기' 칸은 2026-08-18 부터 있었지만, 거기 담기는 것은
 * ⑤ 우측 레일 팝오버에서 고른 **레일 메뉴**뿐이었다. 그래서 프로젝트 홈에서는 늘
 * "등록한 즐겨찾기가 없어요." 만 보였다 — 등록할 방법이 그 화면에 없었기 때문이다.
 * 이 버튼이 그 빠진 한 걸음이다: 스페이스·프로젝트 옆의 ☆ 를 누르면 바로 담긴다.
 *
 * 저장소는 **기존 favoritesStore 를 그대로 쓴다**(신규 store·API·DB 0).
 * 그 store 의 저장 단위가 `{to, label}` — 즉 "경로를 가리키는 포인터" 라, 레일 메뉴든
 * 스페이스든 같은 목록에 섞여도 구조가 깨지지 않는다. 덕분에 여기서 담은 스페이스가
 * 레일 팝오버에도 그대로 보인다(팝오버는 카탈로그에 없는 항목이면 별 아이콘으로 폴백).
 *
 * ⚠ 그래서 저장 위치도 store 를 따라 **브라우저 localStorage** 다 — 기기마다 따로 남는다.
 *   서버 저장이 필요해지면 store 의 read/write 두 함수만 갈아 끼우면 이 버튼은 그대로다.
 *
 * 문구를 여기서 직접 들고 있는 이유: 같은 폴더의 FavoritesPopover 와 같은 규칙이다.
 * 페이지는 `to`·`label` 이라는 **데이터만** 넘기고, 사람에게 보이는 말은 이 컴포넌트가 만든다.
 */
interface FavoriteStarProps {
  /** 즐겨찾기가 가리킬 내부 경로. 중복·해제 판정 기준이다(store 와 동일). */
  to: string;
  /** 등록 시점의 이름 — 목록에 그대로 뜬다. */
  label: string;
  size?: number;
  /** 붙는 자리마다 위치/크기가 달라서 바깥에서 한 겹 더 줄 수 있게 열어 둔다. */
  className?: string;
}

export function FavoriteStar({ to, label, size = 16, className }: FavoriteStarProps) {
  const items = useFavoritesStore((s) => s.items);
  const add = useFavoritesStore((s) => s.add);
  const remove = useFavoritesStore((s) => s.remove);

  const on = items.some((f) => f.to === to);
  // 상한에 걸린 것과 이미 담긴 것은 다르다 — 담긴 것은 언제든 해제할 수 있어야 한다.
  const full = !on && items.length >= FAVORITES_MAX;

  const action = on ? '즐겨찾기 해제' : '즐겨찾기 추가';

  return (
    <button
      type="button"
      className={`fav-star${on ? ' fav-star--on' : ''}${className ? ` ${className}` : ''}`}
      aria-pressed={on}
      aria-label={`${label} ${action}`}
      // 눌리지 않을 때 이유를 말한다 — 회색 버튼만 남으면 "왜 안 되지" 가 된다.
      data-tip={full ? `즐겨찾기는 최대 ${FAVORITES_MAX}개까지 등록할 수 있습니다` : action}
      disabled={full}
      onClick={(e) => {
        // 카드·행 위에 얹히는 자리가 많다 — 별을 눌렀는데 화면이 이동하면 안 된다.
        e.preventDefault();
        e.stopPropagation();
        if (on) remove(to);
        else add({ to, label });
      }}
    >
      <Star size={size} fill={on ? 'currentColor' : 'none'} aria-hidden />
    </button>
  );
}
